import React from 'react';
import './CharacterClassProgressionEditor.css';

const toClassLevel = (value) => Math.max(1, Math.min(20, parseInt(value, 10) || 1));

const toHitDieSize = (value) => {
  const match = String(value || '').trim().match(/(?:^|\+)\s*(?:\d+)?d(4|6|8|10|12|20)\b/i);
  return match ? `d${match[1]}` : String(value || '');
};

/**
 * Returns the canonical class progression used by the editor and print projection.
 * date_of_change: 2026-08-08
 * rule_reference: D&D 5e multiclass characters retain one class-level record per class;
 * total character level and proficiency bonus derive from those records.
 */
export const normalizeCharacterClassProgression = (character) => {
  const recorded = Array.isArray(character?.classes)
    ? character.classes.filter((entry) => entry && typeof entry === 'object')
    : [];

  if (recorded.length > 0) {
    return recorded.map((entry) => ({
      ...entry,
      name: String(entry.name || entry.className || entry.class || ''),
      subclass: String(entry.subclass || entry.subclassName || ''),
      level: toClassLevel(entry.level),
      hitDice: String(entry.hitDice || entry.hitDie || entry.hitDiceType || '')
    }));
  }

  return [{
    name: String(character?.class || ''),
    subclass: String(character?.subclass || ''),
    level: toClassLevel(character?.level),
    hitDice: toHitDieSize(character?.hitDice || character?.hitDie)
  }];
};

const CharacterClassProgressionEditor = ({ character, onChange }) => {
  const rows = normalizeCharacterClassProgression(character);
  const totalLevel = rows.reduce((sum, entry) => sum + toClassLevel(entry.level), 0);

  const updateRow = (index, field, value) => {
    const otherLevels = rows.reduce((sum, entry, entryIndex) => (
      entryIndex === index ? sum : sum + toClassLevel(entry.level)
    ), 0);
    const nextRows = rows.map((entry, entryIndex) => entryIndex === index
      ? {
          ...entry,
          [field]: field === 'level'
            ? Math.min(Math.max(1, 20 - otherLevels), toClassLevel(value))
            : value
        }
      : entry);
    onChange(nextRows);
  };

  const addClass = () => {
    if (totalLevel >= 20) return;
    onChange([
      ...rows,
      { name: '', subclass: '', level: 1, hitDice: '' }
    ]);
  };

  const removeClass = (index) => {
    if (rows.length <= 1) return;
    onChange(rows.filter((_, entryIndex) => entryIndex !== index));
  };

  return (
    <section className="character-class-progression" aria-labelledby="character-class-progression-title">
      <header className="character-class-progression__header">
        <div>
          <h2 id="character-class-progression-title">Class Progression</h2>
          <p>Record every class separately. Total level and proficiency bonus update automatically.</p>
        </div>
        <label className="character-class-progression__total">
          <span>Total Level</span>
          <input
            type="number"
            value={totalLevel}
            readOnly
            title="Calculated from the class levels below."
            aria-label="Total character level"
          />
        </label>
      </header>

      <div className="character-class-progression__rows">
        {rows.map((entry, index) => (
          <div className="character-class-progression__row" key={`class-progression-${index}`}>
            <label>
              <span>Class</span>
              <input
                type="text"
                value={entry.name}
                placeholder="Rogue"
                aria-label={`Class ${index + 1} name`}
                onChange={(event) => updateRow(index, 'name', event.target.value)}
              />
            </label>
            <label>
              <span>Subclass</span>
              <input
                type="text"
                value={entry.subclass}
                placeholder="Arcane Trickster"
                aria-label={`Class ${index + 1} subclass`}
                onChange={(event) => updateRow(index, 'subclass', event.target.value)}
              />
            </label>
            <label className="character-class-progression__level">
              <span>Level</span>
              <input
                type="number"
                min="1"
                max={Math.max(1, 20 - (totalLevel - toClassLevel(entry.level)))}
                value={entry.level}
                aria-label={`Class ${index + 1} level`}
                onChange={(event) => updateRow(index, 'level', event.target.value)}
              />
            </label>
            <label className="character-class-progression__hit-die">
              <span>Hit Die</span>
              <input
                type="text"
                value={entry.hitDice}
                placeholder="d8"
                aria-label={`Class ${index + 1} hit die`}
                onChange={(event) => updateRow(index, 'hitDice', event.target.value)}
              />
            </label>
            <button
              type="button"
              className="character-class-progression__remove"
              disabled={rows.length <= 1}
              title={rows.length <= 1 ? 'A character needs at least one class.' : `Remove ${entry.name || 'this class'}`}
              aria-label={`Remove class ${index + 1}`}
              onClick={() => removeClass(index)}
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="character-class-progression__add"
        disabled={totalLevel >= 20}
        title={totalLevel >= 20 ? 'Total character level is already 20.' : 'Add another class.'}
        onClick={addClass}
      >
        Add another class
      </button>
    </section>
  );
};

export default CharacterClassProgressionEditor;
